/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import {
  useState,
  useMemo,
  useCallback,
  type ChangeEvent,
  type FormEvent,
  type UIEvent,
} from 'react';

import { Divider, Form, SingleDatePicker } from '@mairistem/composants';

import { TYPE_ACTE_OPTIONS, SOUS_TYPE_ACTE_OPTIONS } from '@/constants';
import type { useActes } from '@/hooks';
import type { Acte, TypeActe } from '@/types';
import { stringToAPIDateFormat } from '@/utils';

import ActeTable from './ActeTable';

interface Props {
  typeActe: TypeActe;
  actes: ReturnType<typeof useActes>;
  selectedActe: Acte | null;
  onTypeActeChange: (typeActe: TypeActe) => void;
  onActeSelected: (acte: Acte) => void;
  onNewSearch: () => void;
}

const ActeBlock = ({
  typeActe,
  actes,
  selectedActe,
  onTypeActeChange,
  onActeSelected,
  onNewSearch,
}: Props) => {
  const [sousTypeActe, setSousTypeActe] = useState('');
  const [page, setPage] = useState(1);
  const [params, setParams] = useState<Record<string, unknown>>({});

  const [dateEventField, nomClassementField] = useMemo(() => {
    switch (typeActe) {
      case 'naissance':
        return ['dateNaissance', 'nomClassementEnfant'];
      case 'reconnaissance':
        return ['dateReconnaissancePere', 'nomClassementEnfant'];
      case 'deces':
        return ['dateDeces', 'nomClassementDefunt'];
      case 'mariage':
        return ['dateMariage', 'conjoint1'];
      case 'pacs':
        return ['dateEnregistrement', 'partenaire1NomClassement'];
      case 'decision':
        return ['dateNaissance', 'nomClassementEnfant'];
      default:
        return ['', ''];
    }
  }, [typeActe]);

  const handleTypeActeChange = useCallback(
    (_e: ChangeEvent<HTMLInputElement>, data: Record<string, TypeActe>) => {
      onTypeActeChange(data.value as TypeActe);
      setSousTypeActe('');
      setParams({});
      setPage(1);
      actes.reset();
      onNewSearch();
    },
    [onTypeActeChange, onNewSearch, actes],
  );

  const handleSousTypeActeChange = useCallback(
    (_e: ChangeEvent<HTMLInputElement>, data: Record<string, string>) => {
      setSousTypeActe(data.value);
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.target as HTMLFormElement);
      const data = Object.fromEntries(formData);

      const search: Record<string, unknown> = {
        ...data,
        ...(typeActe === 'pacs'
          ? {}
          : typeActe !== 'reconnaissance'
            ? { codeTypeActe: sousTypeActe }
            : { codeSousTypeActe: sousTypeActe }),
        ...(!_.isEmpty(data?.conjoint1)
          ? { conjoint1: `contains(${data?.conjoint1})` }
          : {}),
        ...(!_.isEmpty(data?.nomClassementDefunt)
          ? { nomClassementDefunt: `contains(${data?.nomClassementDefunt})` }
          : {}),
        ...(!_.isEmpty(data?.nomClassementEnfant)
          ? { nomClassementEnfant: `contains(${data?.nomClassementEnfant})` }
          : {}),
        ...(!_.isEmpty(data?.partenaire1NomClassement)
          ? {
              partenaire1NomClassement: `contains(${data?.partenaire1NomClassement})`,
            }
          : {}),
      };

      if (!_.isEmpty(data?.[dateEventField])) {
        search[dateEventField] = stringToAPIDateFormat(
          data[dateEventField] as string,
        );
      }

      setParams(search);
      setPage(1);
      onNewSearch();
      actes.reset();
      actes.get(search);
    },
    [sousTypeActe, dateEventField, onNewSearch, actes, typeActe],
  );

  const handleScroll = ({ currentTarget }: UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = currentTarget;

    if (
      scrollHeight - scrollTop <= clientHeight &&
      page < (actes.meta?.lastPage ?? 0)
    ) {
      actes.get({ ...params, page: page + 1 });
      setPage((prev) => prev + 1);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Select
            width={6}
            required
            label="Type d'acte"
            options={TYPE_ACTE_OPTIONS}
            placeholder="Choisissez le type d'acte"
            value={typeActe}
            onChange={handleTypeActeChange}
          />
          {typeActe !== 'pacs' && (
            <Form.Dropdown
              width={10}
              clearable
              selection
              label="Sous-type d'acte"
              placeholder="Choisissez le sous-type d'acte"
              name="sousTypeActe"
              options={SOUS_TYPE_ACTE_OPTIONS[typeActe]}
              value={sousTypeActe}
              onChange={handleSousTypeActeChange}
            />
          )}
        </Form.Group>
        <Form.Group>
          <Form.Input
            width={7}
            name={nomClassementField}
            label="Recherche NOM Prénom"
            placeholder="Saisissez le NOM et le Prénom à rechercher"
          />
          <Form.Input
            width={3}
            label="Année"
            placeholder="AAAA"
            name="anneeActe"
            maxLength="4"
          />
          <Form.Input
            width={3}
            label="Numéro"
            placeholder="XXXX"
            name="numeroActe"
            maxLength="4"
          />
          <Form.Field width={3}>
            <label htmlFor={dateEventField}>
              {typeActe === 'pacs'
                ? "Date d'enregistrement"
                : "Date d'événement"}
            </label>
            <SingleDatePicker
              id={dateEventField}
              name={dateEventField}
              clearable
              fluid
              placeholder="dd/mm/yyyy"
            />
          </Form.Field>
        </Form.Group>
        <Form.Button color="blue" content="Rechercher" type="submit" />
      </Form>
      <Divider />
      <div style={{ flex: 1, minHeight: 0 }}>
        <ActeTable
          actes={actes.data}
          loading={actes.loading}
          typeActe={typeActe}
          selectedActe={selectedActe}
          onSelected={onActeSelected}
          onScroll={handleScroll}
        />
      </div>
    </>
  );
};

export default ActeBlock;
